import { rem, useToasts } from "@certn/cascadia";
import { useAPIRequest, usePeopleAPI } from "@certn/legacy-api";
import { components } from "@certn/legacy-api/generated-schemas/people";
import { css } from "@emotion/css";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useTimeOfDay } from "./hooks";
import { compareDates } from "./utils";
import { AppTitle } from "../../components/AppTitle";
import { EmptyState } from "../../components/EmptyState/EmptyState";
import { PortalCard } from "../../components/PortalCard/PortalCard";
import { useUser } from "../../hooks/api/useUser";
import { getPrimaryName } from "../../utils/user-utils";

const isApplicationActive = ({
  expiry_date,
}: components["schemas"]["ApplicationsList"]) =>
  !expiry_date || new Date(expiry_date) > new Date();

const isApplicationStarted = ({
  activity_log,
}: components["schemas"]["ApplicationsList"]) =>
  activity_log[activity_log.length - 1].status !== "SENT";

const isApplicationUpgraded = ({
  activity_log,
}: components["schemas"]["ApplicationsList"]) =>
  activity_log[activity_log.length - 1].status === "UPGRADED";

const getCardType = (
  application: components["schemas"]["ApplicationsList"]
) => {
  if (!isApplicationActive(application)) {
    return "expired";
  }

  if (!isApplicationStarted(application)) {
    return "ready";
  }

  if (isApplicationUpgraded(application)) {
    return "upgrade";
  }

  return "progress";
};

export function DashboardCards() {
  const { t } = useTranslation();
  const { user } = useUser();

  const userName = user && getPrimaryName(user);
  const { data: applications, mutate } = usePeopleAPI(
    "/internal/people/applicant/list/"
  );
  const navigate = useNavigate();
  const request = useAPIRequest();

  const { show } = useToasts();

  const actionableApplications = (applications ?? []).filter(
    ({ activity_log, archived }) =>
      !archived &&
      ["SENT", "STARTED", "UPGRADED"].includes(
        activity_log[activity_log.length - 1]?.status
      )
  );
  const activeApplications = actionableApplications.filter(isApplicationActive);
  const sortedApplications = actionableApplications.sort(compareDates);
  const timeGreeting = useTimeOfDay();
  const fullGreeting = `${timeGreeting}, ${userName?.first_name}`;

  const handlePress = async (
    application: components["schemas"]["ApplicationsList"]
  ) => {
    if (!isApplicationActive(application)) {
      try {
        await request(
          `/internal/people/applicant/${application.applicant_id}/archive/`,
          { method: "POST" }
        );
        await mutate();
      } catch (e) {
        console.error("Error archiving application: ", e);
        show({
          title: t("Something went wrong, try again"),
          type: "error",
        });
      }
      return;
    }

    navigate(`/apply/${application.applicant_id}/`);
  };

  if (actionableApplications.length === 0) {
    return (
      <div>
        <AppTitle
          title={fullGreeting}
          subtitle={t("You're all caught up.")}
          className={styles.title}
        />
        <EmptyState
          illustration="check-todo"
          description={
            <Trans>
              When someone sends you a background check, it will appear here.
            </Trans>
          }
        />
      </div>
    );
  }

  const backgroundCheckText =
    activeApplications.length === 1
      ? t("background check")
      : t("background checks");

  return (
    <section aria-label="applications">
      <AppTitle
        title={fullGreeting}
        subtitle={t("You have {{num}} {{backgroundCheckText}} to complete.", {
          num: activeApplications.length,
          backgroundCheckText,
        })}
        className={styles.title}
      />
      {sortedApplications.map(
        (
          {
            application,
            received_date,
            expiry_date,
            applicant_id,
            has_been_submitted,
          },
          i
        ) => (
          <PortalCard
            key={i}
            applicantId={applicant_id}
            title={
              application.team?.superteam?.name ?? application.team?.name ?? "-"
            }
            type={getCardType(sortedApplications[i])}
            expirationDate={expiry_date ? new Date(expiry_date) : undefined}
            canDeclineApplication={!has_been_submitted}
            receivedDate={new Date(received_date)}
            onPress={() => handlePress(sortedApplications[i])}
            teamName={application.team?.name}
          />
        )
      )}
    </section>
  );
}

const styles = {
  title: css({
    marginBottom: rem("space-8"),
  }),
};
